import { cva, type VariantProps } from 'class-variance-authority'
import { Text as AriaText, type TextProps as AriaTextProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link HelpText}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports three variants: hint (default), error, success
 * - Size follows text-ds-sm for helper text
 * - Error variant uses danger color from semantic tokens
 * - Success variant uses success color from semantic tokens
 *
 * @example
 * ```tsx
 * const classes = helpTextVariants({ variant: 'error' })
 * // Returns: "text-ds-sm text-ds-danger mt-1"
 * ```
 */
export const helpTextVariants = cva('text-ds-sm mt-1', {
	variants: {
		variant: {
			hint: 'text-ds-text-muted',
			error: 'text-ds-danger',
			success: 'text-ds-success',
		},
	},
	defaultVariants: { variant: 'hint' },
})

/**
 * Props for the {@link HelpText} component.
 *
 * Extends React Aria's TextProps with CVA variant styles.
 *
 * @property variant - Visual variant: 'hint' (default) | 'error' | 'success'
 * @property className - Optional additional Tailwind classes
 * @property children - Help text content
 *
 * @example
 * ```tsx
 * <HelpText variant="hint">
 *   Enter a valid email address
 * </HelpText>
 * ```
 */
export interface HelpTextProps extends Omit<AriaTextProps, 'className'>, VariantProps<typeof helpTextVariants> {
	className?: string
}

/**
 * Accessible help/hint text primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Uses React Aria's `<Text>` with proper `slot="description"`
 *   or `slot="errorMessage"` for automatic `aria-describedby` association.
 * - **Styling:** All colors and sizing use design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Variants:** Hint (muted gray), error (danger red), success (success green).
 * - **Usage:** Place below form fields to provide contextual help or validation feedback.
 *
 * @example Basic usage (hint text)
 * ```tsx
 * import { HelpText } from '@today/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <div>
 *       <Label htmlFor="email">Email</Label>
 *       <Input id="email" type="email" />
 *       <HelpText>We'll never share your email</HelpText>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example Error message
 * ```tsx
 * <HelpText variant="error">
 *   Email address is required
 * </HelpText>
 * ```
 *
 * @example Success message
 * ```tsx
 * <HelpText variant="success">
 *   Email is valid
 * </HelpText>
 * ```
 *
 * @see {@link helpTextVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/help-text/help-text.stories.tsx for visual examples
 */
export function HelpText({ variant, className, ...props }: HelpTextProps) {
	return <AriaText {...props} slot="description" className={cn(helpTextVariants({ variant }), className)} />
}
